"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import LinkHandlerFrame from "@/components/waves/LinkHandlerFrame";
import { DropLocation } from "@/components/waves/drops/drop.types";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import WaveDropQuote from "@/components/waves/drops/WaveDropQuote";
import { WaveDropsSearchStrategy } from "@/contexts/wave/hooks/types";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiWaveDropsFeed } from "@/generated/models/ApiWaveDropsFeed";
import { convertApiDropToExtendedDrop, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DROP_DETAIL_STALE_TIME_MS, fetchDropByIdBatched, getDropQueryKey } from "@/services/api/drop-api";
import { commonApiFetch } from "@/services/api/common-api";
import QuorumParticipationDrop from "./QuorumParticipationDrop";
interface QuorumParticipationDropLinkPreviewProps {
  readonly href: string;
  readonly waveId: string;
  readonly dropId?: string | undefined;
  readonly serialNo?: string | number | undefined;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
  readonly hideLink?: boolean | undefined;
}
const toSerialNumber = (serialNo: string | number | undefined): number | null => {
  if (typeof serialNo === "number") {
    return Number.isFinite(serialNo) ? serialNo : null;
  }
  if (!serialNo) {
    return null;
  }
  const parsed = Number.parseInt(serialNo, 10);
  return Number.isFinite(parsed) ? parsed : null;
};
const fetchDropBySerialNo = async ({
  waveId,
  serialNo
}: {
  readonly waveId: string;
  readonly serialNo: number;
}): Promise<ApiDrop | null> => {
  const results = await commonApiFetch<ApiWaveDropsFeed>({
    endpoint: `waves/${waveId}/drops`,
    params: {
      limit: "1",
      serial_no_limit: `${serialNo}`,
      search_strategy: WaveDropsSearchStrategy.Both
    }
  });
  const drop = results.drops.find(candidate => candidate.serial_no === serialNo);
  if (!drop) {
    return null;
  }
  return {
    ...drop,
    wave: results.wave
  } as ApiDrop;
};
export default function QuorumParticipationDropLinkPreview(t0) {
  const $ = _c(31);
  const {
    href,
    waveId,
    dropId,
    serialNo,
    onQuoteClick,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth,
    hideLink
  } = t0;
  let t1;
  if ($[0] !== serialNo) {
    t1 = toSerialNumber(serialNo);
    $[0] = serialNo;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const parsedSerialNo = t1;
  const normalizedDropId = dropId === undefined || dropId.length === 0 ? null : dropId;
  let t2;
  if ($[2] !== normalizedDropId || $[3] !== parsedSerialNo || $[4] !== waveId) {
    t2 = normalizedDropId !== null ? getDropQueryKey(normalizedDropId) : [QueryKey.DROP, "quorum-participation-link-preview", {
      dropId: normalizedDropId,
      waveId,
      serialNo: parsedSerialNo
    }];
    $[2] = normalizedDropId;
    $[3] = parsedSerialNo;
    $[4] = waveId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== normalizedDropId || $[7] !== parsedSerialNo || $[8] !== waveId) {
    t3 = async () => {
      if (normalizedDropId !== null) {
        return await fetchDropByIdBatched(normalizedDropId);
      }
      if (parsedSerialNo !== null) {
        return await fetchDropBySerialNo({
          waveId,
          serialNo: parsedSerialNo
        });
      }
      return null;
    };
    $[6] = normalizedDropId;
    $[7] = parsedSerialNo;
    $[8] = waveId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = normalizedDropId !== null || parsedSerialNo !== null;
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      placeholderData: keepPreviousData,
      staleTime: DROP_DETAIL_STALE_TIME_MS
    };
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    data: drop
  } = useQuery(t5);
  let t6;
  bb0: {
    if (drop?.drop_type !== ApiDropType.Participatory) {
      t6 = null;
      break bb0;
    }
    let t7;
    if ($[14] !== drop) {
      t7 = convertApiDropToExtendedDrop(drop);
      $[14] = drop;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    t6 = t7;
  }
  const extendedDrop = t6;
  const handleReply = _temp;
  let t7;
  if ($[16] !== onQuoteClick) {
    t7 = selectedDrop => onQuoteClick(selectedDrop);
    $[16] = onQuoteClick;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleDropContentClick = t7;
  let t8;
  if ($[18] !== drop || $[19] !== embedDepth || $[20] !== embedPath || $[21] !== extendedDrop || $[22] !== handleDropContentClick || $[23] !== maxEmbedDepth || $[24] !== onQuoteClick || $[25] !== quotePath) {
    t8 = extendedDrop ? <QuorumParticipationDrop drop={extendedDrop} showWaveInfo={true} activeDrop={null} showReplyAndQuote={false} location={DropLocation.MY_STREAM} onReply={handleReply} onQuoteClick={onQuoteClick} onDropContentClick={handleDropContentClick} showInteractions={false} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} /> : <WaveDropQuote drop={drop ?? null} partId={1} onQuoteClick={onQuoteClick} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[18] = drop;
    $[19] = embedDepth;
    $[20] = embedPath;
    $[21] = extendedDrop;
    $[22] = handleDropContentClick;
    $[23] = maxEmbedDepth;
    $[24] = onQuoteClick;
    $[25] = quotePath;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== hideLink || $[28] !== href || $[29] !== t8) {
    t9 = <LinkHandlerFrame href={href} hideLink={hideLink}>{t8}</LinkHandlerFrame>;
    $[27] = hideLink;
    $[28] = href;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
function _temp(_params) {}